import React, { useCallback, useState } from 'react';
import { CardOrientationEnum } from '../BasePostCard/BasePostCard.view';
import { LinkFieldsFragment, CorePhotoFieldsFragment, CoreVideoFieldsFragment } from '../../../models/graphql';
import classNames from 'classnames';
import classes from './PostCardMedia.module.scss';
import { Icon } from '../../images/Icon/Icon';
import { ReactComponent as NoImage } from '../../../assets/images/linkPreviewNoImage.svg';
import { IconsEnum } from '../../../constants/icons';
import { Debugger } from '../../containers/Debugger';

export type PostCardSingleMediaProps = {
  type: 'Single';
  media: CorePhotoFieldsFragment[] | CoreVideoFieldsFragment[];
};

export type PostCardStoryMediaProps = {
  type: 'Story';
  version: 'Story1' | 'Story2' | 'Story3';
  media: CorePhotoFieldsFragment[] | CoreVideoFieldsFragment[];
};

export type PostCardPreviewMediaProps = {
  type: 'Preview';
  link: LinkFieldsFragment;
};

export type PostCardCarouselMediaProps = {
  type: 'Carousel';
  media: CorePhotoFieldsFragment[];
};

type PostCardType =
  | PostCardSingleMediaProps
  | PostCardStoryMediaProps
  | PostCardPreviewMediaProps
  | PostCardCarouselMediaProps;

export type PostCardMediaViewProps = PostCardType & {
  orientation: CardOrientationEnum;
};

export const PostCardMediaView = (props: PostCardMediaViewProps): JSX.Element => {
  const { type, orientation } = props;
  const version = type === 'Story' ? props.version : undefined;
  const media = type !== 'Preview' ? (props.media.length > 0 ? props.media : undefined) : undefined;
  const link = type === 'Preview' ? props.link : undefined;
  const [loaded, setLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const cardMediaClasses = classNames('cell shrink margin-top-small-8 margin-bottom-small-8', {
    [classes.SingleMedia]: type === 'Single',
    [classes.Story1Media]: type === 'Story' && version === 'Story1',
    [classes.Story2Media]: type === 'Story' && version === 'Story2',
    [classes.Story3Media]: type === 'Story' && version === 'Story3',
    [classes.SingleMedia]: type === 'Preview',
    [classes.SingleMedia]: type === 'Carousel'
  });

  const storyMediaElement = useCallback(() => {
    let mediaLeft = '';
    let mediaCenter = '';
    let mediaRight = '';

    switch (version) {
      case 'Story1':
        mediaCenter = media ? media[0].thumbnailUrl : '';
        break;
      case 'Story2':
        mediaLeft = media ? media[0].thumbnailUrl : '';
        mediaRight = media ? media[1].thumbnailUrl : '';
        break;
      case 'Story3':
        mediaLeft = media ? media[0].thumbnailUrl : '';
        mediaCenter = media ? media[1].thumbnailUrl : '';
        mediaRight = media ? media[2].thumbnailUrl : '';
        break;
    }

    return (
      <>
        {type === 'Story' && media && (
          <div className={cardMediaClasses}>
            <div className={'grid-x'}>
              <>
                {version !== 'Story1' && (
                  <div className={`cell auto ${classes.Left}`}>
                    <img src={mediaLeft} alt={'story 1'} />
                  </div>
                )}
              </>
              {version !== 'Story2' && (
                <div className={'cell shrink'}>
                  <img src={mediaCenter} alt={'story 2'} />
                </div>
              )}
              <>
                {version !== 'Story1' && (
                  <div className={`cell auto ${classes.Right}`}>
                    <img src={mediaRight} alt={'story 3'} />
                  </div>
                )}
              </>
            </div>
          </div>
        )}
      </>
    );
  }, [cardMediaClasses, media, type, version]);

  const SingleMediaElement = useCallback(() => {
    const wrapperClasses = classNames(classes.PostCardMedia, {
      [classes.PostCardMediaPortrait]: orientation === CardOrientationEnum.PORTRAIT,
      [classes.PostCardMediaLandscape]: orientation === CardOrientationEnum.LANDSCAPE
    });
    const cardMediaClasses = classNames('cell shrink margin-top-small-8 margin-bottom-small-8', classes.SingleMedia, {
      [classes.SingleMediaLoaded]: !loaded
    });

    return (
      <>
        {media && media[0] ? (
          <div className={wrapperClasses}>
            <div className={cardMediaClasses}>
              <Debugger tooltipContent={'Media ID: ' + media[0].id} modalContent={media[0]} />
              {media[0].__typename === 'Video' && (
                <div className={classes.MediaTypeIcon}>
                  <Icon name={IconsEnum.video} size={'medium'} />
                </div>
              )}
              {media.length > 1 && (
                <div className={classes.MediaTypeIcon}>
                  <Icon name={IconsEnum.carousel} size={'medium'} />
                </div>
              )}
              {media[0].thumbnailUrl && !imageError ? (
                <img
                  alt={'media'}
                  src={media[0].thumbnailUrl}
                  onLoad={() => setLoaded(true)}
                  onError={() => setImageError(true)}
                />
              ) : media[0].url && !imageError ? (
                <img
                  alt={'media'}
                  src={media[0].url}
                  onLoad={() => setLoaded(true)}
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className={classes.PlaceHolder}>
                  <NoImage />
                </div>
              )}
            </div>
          </div>
        ) : (
          <></>
        )}
      </>
    );
  }, [imageError, loaded, media, orientation]);

  const LinkPreviewElement = useCallback(() => {
    const cardMediaClasses = classNames('cell shrink margin-top-small-8 margin-bottom-small-8', classes.SingleMedia);

    return (
      <>
        {link && (
          <div className={cardMediaClasses}>
            <Debugger tooltipContent={'Link ID: ' + link.id} modalContent={link} />
            <div className={classes.LinkPreviewNoImageContainer}>
              <NoImage />
            </div>
            <div className={classes.LinkPreviewData}>
              <div className={classes.LinkPreviewTitle}>{link.caption && link.caption}</div>
              <div className={classes.LinkPreviewLink}>{link.url && <a href={link.url}>{link.url}</a>}</div>
            </div>
          </div>
        )}
      </>
    );
  }, [link]);

  switch (type) {
    case 'Single':
      return SingleMediaElement();
    case 'Story':
      return storyMediaElement();
    case 'Preview':
      return LinkPreviewElement();
  }

  return <></>;
};
